import { Box, Link, Tooltip, Typography } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Link as RouterLink } from 'react-router-dom';
import { SectionCard } from '../../markets/components/shared';
import { NEW_TAB_LINK_PROPS } from '../../../utils/newTab';
import { binColor } from '../chart/palette';
import {
    binLabel,
    deltaLabel,
    localDate,
    pct,
    pctInt,
    tsDate,
    utcDateTime,
} from '../chart/format';
import { toneColor } from './layers/decisionTone';
import { eventPath } from './links';
import { S } from './strings';
import type { Meeting, PiprBundle } from './types';

interface PiprMeetingsProps {
    data: PiprBundle;
    range: readonly [Date, Date];
    highlight: number | null;
    ink: string;
}

interface Row {
    idx: number;
    meeting: Meeting;
    status: string;
    statusColor: string;
    rate: string | null;
    p: number[] | null;
    mark: number | null;
    note: string;
}

export function beforeText(seconds: number): string {
    const minutes = Math.max(1, Math.round(seconds / 60));
    if (minutes < 90) return S.minutes.replace('{n}', String(minutes));
    const hours = Math.round(minutes / 60);
    if (hours < 48) return S.hours.replace('{n}', String(hours));
    return S.days.replace('{n}', String(Math.round(hours / 24)));
}

function binIndexOf(deltas: readonly number[], bp: number): number | null {
    if (deltas.length === 0) return null;
    const clamped = Math.max(
        deltas[0],
        Math.min(deltas[deltas.length - 1], bp)
    );
    const j = deltas.indexOf(clamped);
    return j >= 0 ? j : null;
}

function decisionText(delta: number): string {
    if (delta === 0) return S.hold;
    return `${delta > 0 ? S.hike : S.cut} (${deltaLabel(delta)})`;
}

export function buildRows(
    data: PiprBundle,
    range: readonly [Date, Date],
    ink: string
): Row[] {
    const startSec = range[0].getTime() / 1000;
    const deltas = data.history.deltas;
    const fwdByTs = new Map(data.forward.meetings.map(m => [m.decisionTs, m]));
    const rows: Row[] = [];
    data.meetings.forEach((meeting, idx) => {
        const fwd = fwdByTs.get(meeting.decisionTs);
        const decided =
            meeting.decidedBp !== null &&
            meeting.decisionTs <= data.forward.asofTs;
        if (!fwd && (!decided || meeting.decisionTs < startSec)) return;
        if (decided && meeting.decidedBp !== null) {
            const f = meeting.final;
            const j = binIndexOf(deltas, meeting.decidedBp);
            const before = f ? beforeText(meeting.decisionTs - f.ts) : '';
            const priced =
                f && j !== null
                    ? ` · ${pctInt(f.p[j])} ${S.pricedBefore.replace('{t}', before)}`
                    : '';
            rows.push({
                idx,
                meeting,
                status: `${S.decisionMade}: ${decisionText(meeting.decidedBp)}${priced}`,
                statusColor: toneColor(meeting.decidedBp, ink),
                rate: f
                    ? pct(f.anchorBp + f.pendingBp + meeting.decidedBp, 2)
                    : null,
                p: f ? f.p : null,
                mark: j,
                note: f
                    ? S.pricedAt
                          .replace('{d}', utcDateTime(tsDate(f.ts)))
                          .replace('{t}', before)
                    : '',
            });
            return;
        }
        if (fwd) {
            let k = 0;
            for (let j = 1; j < fwd.p.length; j++)
                if (fwd.p[j] > fwd.p[k]) k = j;
            rows.push({
                idx,
                meeting,
                status: `${S.decisionLikely}: ${decisionText(deltas[k])} · ${pctInt(fwd.p[k])}`,
                statusColor: toneColor(deltas[k], ink),
                rate: pct(fwd.rateBp, 2),
                p: fwd.p,
                mark: k,
                note: S.expectedAt,
            });
        }
    });
    return rows.reverse();
}

export function PiprMeetings({
    data,
    range,
    highlight,
    ink,
}: PiprMeetingsProps) {
    const rows = buildRows(data, range, ink);
    const deltas = data.history.deltas;
    if (rows.length === 0) return null;

    return (
        <SectionCard sx={{ p: 1.5 }}>
            <Typography sx={{ fontSize: '13px', fontWeight: 700, mb: 1 }}>
                Meetings
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                {rows.map(row => {
                    const href = eventPath(row.meeting, { range });
                    return (
                        <Box
                            key={row.meeting.meetingDate}
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: {
                                    xs: '1fr auto',
                                    md: '130px 110px minmax(0, 1fr) 90px 160px 120px',
                                },
                                alignItems: 'center',
                                columnGap: 2,
                                rowGap: 0.5,
                                py: 0.75,
                                px: 1,
                                borderTop: '1px solid',
                                borderColor: 'divider',
                                bgcolor:
                                    row.idx === highlight
                                        ? 'action.selected'
                                        : 'transparent',
                            }}
                        >
                            <Typography
                                sx={{ fontSize: '12px', fontWeight: 600 }}
                            >
                                {localDate(tsDate(row.meeting.decisionTs))}
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: '12px',
                                    color: 'text.secondary',
                                    display: { xs: 'none', md: 'block' },
                                }}
                            >
                                {row.meeting.label ?? ''}
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: '12px',
                                    color: row.statusColor,
                                    gridColumn: { xs: '1 / -1', md: 'auto' },
                                    gridRow: { xs: 2, md: 'auto' },
                                }}
                            >
                                {row.status}
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: '12px',
                                    fontVariantNumeric: 'tabular-nums',
                                    display: { xs: 'none', md: 'block' },
                                }}
                            >
                                {row.rate ?? ''}
                            </Typography>
                            <Box
                                sx={{
                                    display: { xs: 'none', md: 'flex' },
                                    gap: '2px',
                                    height: 10,
                                }}
                            >
                                {row.p?.map((q, j) => (
                                    <Tooltip
                                        key={j}
                                        arrow
                                        placement="top"
                                        disableInteractive
                                        title={
                                            <Box sx={{ fontSize: '11px' }}>
                                                <Box sx={{ fontWeight: 600 }}>
                                                    {binLabel(
                                                        deltas[j],
                                                        j,
                                                        deltas.length
                                                    )}{' '}
                                                    · {pctInt(q)}
                                                </Box>
                                                <Box sx={{ opacity: 0.8 }}>
                                                    {row.note}
                                                </Box>
                                            </Box>
                                        }
                                    >
                                        <Box
                                            sx={{
                                                flex: q / 1000,
                                                minWidth: q > 0 ? '3px' : 0,
                                                bgcolor: binColor(deltas[j]),
                                                borderRadius: '2px',
                                                opacity:
                                                    j === row.mark ? 1 : 0.45,
                                                '&:hover': { opacity: 1 },
                                            }}
                                        />
                                    </Tooltip>
                                ))}
                            </Box>
                            <Box sx={{ justifySelf: 'end' }}>
                                {href ? (
                                    <Link
                                        component={RouterLink}
                                        to={href}
                                        {...NEW_TAB_LINK_PROPS}
                                        underline="hover"
                                        sx={{
                                            fontSize: '12px',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 0.5,
                                        }}
                                    >
                                        {S.openMarket}
                                        <ArrowForwardIcon
                                            sx={{ fontSize: 12 }}
                                        />
                                    </Link>
                                ) : (
                                    <Typography
                                        sx={{
                                            fontSize: '12px',
                                            color: 'text.disabled',
                                        }}
                                    >
                                        {S.noMarket}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    );
                })}
            </Box>
        </SectionCard>
    );
}
